<template>
    <div class="is-full-mobile is-narrow is-relative is-app-sidebar tw:h-full">
        <aside id="application_sidebar" class="menu has-text-weight-semibold" :class="{ 'is-expanded': !summarize }">
            <ul v-if="summarize">
                <!--
                    `#summaryIcon` is the collapsed-mode counterpart to
                    `#nodeIcon`. When a parent (e.g. CorporateStudentSidebar)
                    provides it, the slot replaces the default string-icon
                    rendering inside AppSidebarSummaryItem — letting the
                    parent emit a Vue-component icon (Phosphor SFC, custom
                    SVG, etc.) instead of being constrained to the
                    string-based Unicons/pi-* names that `<e-icon :icon>`
                    expects. When the parent does NOT provide it, the
                    wrapping `<template v-if>` collapses to nothing and
                    AppSidebarSummaryItem falls back to its default
                    `<e-icon>` rendering — so every existing consumer
                    (CorporateAdminSidebar, the various Stanbridge sidebars,
                    etc.) keeps its current behavior unchanged.
                -->
                <app-sidebar-summary-item
                    v-for="item in filteredItems"
                    :key="item.key"
                    :class="{ 'home-icon-item': item.data?.isHomeItem }"
                    :type="item.data?.type === 'folder' ? 'is-primary' : 'is-primary-light'"
                    :label="item.label"
                    :icon="item.icon || 'link'"
                    :to="item.data?.route || null"
                    :active="isSummaryItemActive(item)"
                    @open="toggleSummary(item.data, item.key)"
                >
                    <template v-if="$slots.summaryIcon" #icon>
                        <slot name="summaryIcon" :item="item" />
                    </template>
                </app-sidebar-summary-item>
            </ul>

            <!--            <div v-else class="course-menu-container">-->
            <!--                <ul>-->
            <!--                    <li v-for="(item, index) in filteredItems" :key="index">-->
            <!--                        <component-->
            <!--                            :is="`sidebar-${item.type}`"-->
            <!--                            :item="item"-->
            <!--                            :has-icon="hasIcon"-->
            <!--                            :display-items-always="item.displayItemsAlways"-->
            <!--                            :display-items-on-create="itemToOpen === index"-->
            <!--                        />-->
            <!--                    </li>-->
            <!--                </ul>-->
            <!--            </div>-->

            <Tree
                v-if="!summarize"
                v-model:expanded-keys="expandedKeys"
                v-model:selection-keys="selectionKeys"
                class="tree tw:w-full"
                selection-mode="single"
                :value="filteredItems"
                :pt="{
                    nodeContent: {
                        class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer'
                    },
                    nodeLabel: {
                        class: 'tw:flex-1 tw:min-w-0'
                    },
                    nodeChildren: {
                        style: 'padding-inline-start: 1rem !important;'
                    },
                    nodeToggleButton: {
                        class: 'tw:order-last tw:ml-auto!'
                    }
                }"
                @node-select="onNodeSelect"
            >
                <template #default="{ node }">
                    <router-link
                        v-if="node.data?.route"
                        :to="node.data.route"
                        class="tree-node-link"
                        :class="{ 'tree-node-link--active': isTreeNodeActive(node) }"
                    >
                        <slot name="nodeIcon" :node="node" />
                        {{ node.label }}
                    </router-link>
                    <span v-else class="tree-node-folder">
                        <slot name="nodeIcon" :node="node" />
                        {{ node.label }}
                    </span>
                </template>
            </Tree>
        </aside>

        <div class="is-app-sidebar-meta tw:flex tw:flex-col tw:mb-4">
            <brand-icon v-if="institutionIsStanbridge" class="tw:mx-auto" />

            <p>{{ appVersion }}</p>
        </div>
    </div>
</template>

<script>
import filter from 'lodash/filter.js';
import some from 'lodash/some.js';
import AppSidebarSummaryItem from '@/components/app/sidebar/AppSidebarSummaryItem.vue';
import BrandIcon from '@/components/elements/BrandIcon.vue';
import { initialState } from '@/Helpers.js';
import { useLayout } from '@/layouts/composables/layout.js';
import UserGroupMixin from '@/mixins/UserGroup.mixin.js';
import { isRouteExactlyActive, resolveSidebarMenuActiveState } from '@/utilities/sidebarMenuRoute.js';

/**
 * Convert items to PrimeVue Tree node format
 * @param {Object|Array} items - The items to convert
 * @param {String} parentKey - The parent key for generating unique keys
 * @returns {Array} - Array of tree nodes
 */
function convertItemsToNodes(items, parentKey = '') {
    if (!items) return [];

    // Convert to array if items is an object
    const itemsArray = Array.isArray(items) ? items : Object.values(items);

    return itemsArray.map((item, index) => {
        const nodeKey = parentKey ? `${parentKey}-${index}` : `${index}`;
        const childItems = item.items;
        const hasChildren = childItems && Object.keys(childItems).length > 0;
        const node = {
            key: nodeKey,
            label: item.title,
            // eslint-disable-next-line no-nested-ternary
            icon: item.icon ? (item.icon.startsWith('pi ') ? item.icon : `pi pi-${item.icon}`) : undefined,
            data: {
                type: item.type,
                route: item.route || null,
                disabled: item.disabled || false,
                order: item.order,
                ...item,
            },
            leaf: item.type === 'item' || !hasChildren,
        };

        // Recursively convert child items
        if (hasChildren) {
            node.children = convertItemsToNodes(childItems, nodeKey);
        }

        return node;
    });
}

export default {
    name: 'SidebarMenu',

    components: {
        AppSidebarSummaryItem,
        BrandIcon,
    },

    mixins: [UserGroupMixin],

    emits: ['update:expanded'],

    props: {
        items: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },

        activePageFolder: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },

        hasIcon: {
            type: Boolean,
            required: false,
            default: false,
        },

        forceExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },

        includeHome: {
            type: Boolean,
            required: false,
            default: true,
        },

        // When true (default), clicking a tree node with a route collapses
        // the sidebar back to summary view after navigation. This is the
        // long-standing Stanbridge UX. Corporate sidebars opt out so the
        // expanded menu stays visible as the user navigates between
        // top-level corporate pages.
        collapseOnNavigate: {
            type: Boolean,
            required: false,
            default: true,
        },
    },

    setup() {
        const { layoutState, toggleSidebarSummary } = useLayout();

        return {
            layoutState,
            toggleSidebarSummary,
        };
    },

    data: () => ({
        itemToOpen: undefined,
        appVersion: window.appVersion,
        expandedKeys: {},
        selectionKeys: {},
        activeSummaryKey: null,
    }),

    computed: {
        summarize() {
            if (this.forceExpanded) {
                return false;
            }
            // Summary (icon rail) is a desktop affordance; the mobile/tablet
            // drawer is wide and always shows the full expanded menu.
            if (this.$isMobile || this.$isTablet) {
                return false;
            }
            return this.layoutState.sidebarSummaryMode;
        },

        authUser() {
            return this.$store.state.User;
        },

        filteredItems() {
            const homeItem = {
                key: 'home',
                label: 'Home',
                icon: 'pi pi-home',
                data: {
                    type: 'item',
                    route: { name: 'home' },
                    isHomeItem: true,
                },
                selectable: true,
                leaf: true,
            };

            let items = [];

            if (this.$route.name.includes('page')) {
                items = convertItemsToNodes(this.items);
            } else {
                const filteredItemsBeforeConversion = filter(this.items, item => {
                    if (!item.items) {
                        return true;
                    }

                    return some(item.items, child => {
                        if (child.canSee === undefined) {
                            return true;
                        }
                        return child.canSee;
                    });
                });

                items = convertItemsToNodes(filteredItemsBeforeConversion);
            }

            return this.includeHome ? [homeItem, ...items] : items;
        },

        institutionIsStanbridge() {
            return initialState('InstitutionSettings.name') === 'Stanbridge University';
        },
    },

    watch: {
        summarize(newValue) {
            // Emit update when summary state changes (including from topbar button)
            this.$emit('update:expanded', !newValue);
        },

        '$route.fullPath': {
            immediate: true,
            handler() {
                this.syncActiveMenuState();
            },
        },

        filteredItems: {
            immediate: true,
            handler() {
                this.syncActiveMenuState();
            },
        },
    },

    mounted() {
        // Emit initial state
        this.$emit('update:expanded', !this.summarize);

        if (this.itemToOpen) {
            this.expandedKeys = {
                [this.itemToOpen]: true,
            };
        }

        this.syncActiveMenuState();
    },

    methods: {
        toggleSummary(item, key) {
            if (item && item.type === 'folder') {
                this.itemToOpen = key;
            } else {
                this.itemToOpen = undefined;
            }

            this.activeSummaryKey = key;
            this.expandedKeys = {
                [this.itemToOpen]: true,
            };

            this.toggleSidebarSummary(item);
            this.$emit('update:expanded', !this.summarize);
        },

        resetToSummary() {
            // Reset to summary view (collapsed state)
            if (!this.summarize) {
                this.toggleSidebarSummary();
            }
            this.itemToOpen = undefined;
            this.$emit('update:expanded', false);
        },

        /**
         * @param item
         * @returns {*|string}
         */
        getSummaryItemIcon(item) {
            if (item.icon) {
                return item.icon;
            }

            return item.type === 'folder' ? 'folder' : 'link';
        },

        onNodeSelect(node) {
            this.selectionKeys = { [node.key]: true };
            this.activeSummaryKey = node.key;

            if (node.data && node.data.route) {
                this.$router.push(node.data.route);

                if (!this.forceExpanded && this.collapseOnNavigate) {
                    this.resetToSummary();
                }

                return;
            }

            // Toggle expansion for nodes with children
            if (node.children && node.children.length > 0) {
                if (this.expandedKeys[node.key]) {
                    const newKeys = { ...this.expandedKeys };
                    delete newKeys[node.key];
                    this.expandedKeys = newKeys;
                } else {
                    this.expandedKeys = {
                        ...this.expandedKeys,
                        [node.key]: true,
                    };
                }

                this.activeSummaryKey = node.key;
            }
        },

        isSummaryItemActive(item) {
            return this.activeSummaryKey === item.key;
        },

        isTreeNodeActive(node) {
            if (this.selectionKeys[node.key]) {
                return true;
            }

            return isRouteExactlyActive(node.data?.route, this.$route, this.$router);
        },

        syncActiveMenuState() {
            const { selectionKeys, expandedKeys, activeSummaryKey } = resolveSidebarMenuActiveState(
                this.filteredItems,
                this.$route,
                this.$router
            );

            this.selectionKeys = selectionKeys;
            this.activeSummaryKey = activeSummaryKey;

            if (Object.keys(expandedKeys).length > 0) {
                this.expandedKeys = {
                    ...this.expandedKeys,
                    ...expandedKeys,
                };
            }
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.is-app-sidebar {
    display: flex !important;
    flex-direction: column;
    background-color: transparent !important;

    #application_sidebar {
        background-color: transparent !important;
    }

    &-meta {
        margin-top: auto;
        padding-top: $size-5;
        text-align: center;

        .brandIcon {
            margin-bottom: $size-10;
            vertical-align: bottom;
        }

        p {
            font-size: $very-small-font-size;
            line-height: 1;
        }
    }
}

.nested-list.menu-list {
    li.hidden {
        border-top: none;
    }
}

ul.is-unstyled {
    list-style: none;
    margin: 0;
    padding: 0;
}

.course-menu-container {
    width: 100%;
    padding: 0;
    margin: 0;
}

.menu {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.is-expanded {
    align-items: start !important;
    padding-left: 0.95rem;
}

.home-icon-item {
    .e-icon,
    svg,
    i {
        font-size: 1.5rem !important;
        width: 1.5rem !important;
        height: 1.5rem !important;
    }

    .sidebar-badge-container {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
}

.tree :deep(.p-tree-node-content) {
    padding: 0;
    padding-block: 0.25rem;
}

.tree :deep(.p-tree-node-leaf .p-tree-node-toggle-button),
.tree :deep(.p-tree-node-toggle-button[data-p-leaf='true']) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
}

.tree-node-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: inherit;
    width: 100%;
    padding-block: 0.25rem;

    &:hover {
        text-decoration: none;
    }

    &.router-link-active,
    &.tree-node-link--active {
        font-weight: bold;
    }
}

.tree-node-folder {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding-block: 0.25rem;
}

.tree :deep(.p-tree-node-content.p-tree-node-selected) {
    background-color: var(--surface-hover);
}
</style>
